import axios from 'axios';
import {
  GET_SLA_RUNTIMES_URL,
  UPDATE_SLA_RUNTIME_STATUS_URL,
  GET_SLA_ASSIGNED_WORKFLOWS_URL,
  GET_SLA_TRIGGERS_URL,
  LIST_SLA_CONFIGS_URL,
  get_request_config,
} from '@/utils/system_constants/constant.js';
import { getXVerdictFullConfigByEntityType } from './xverdict_entity_config_service';

const JSON_START_CHARS = new Set(['{', '[', '"', '-', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 't', 'f', 'n']);
const sla_cache = new Map();
const ALLOWED_CACHED_GET_URLS = new Set([
  LIST_SLA_CONFIGS_URL,
  GET_SLA_ASSIGNED_WORKFLOWS_URL,
  GET_SLA_TRIGGERS_URL,
  GET_SLA_RUNTIMES_URL,
]);

function parse_json(value, fallback = {}) {
  try {
    return should_parse_json(value) ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
}

function should_parse_json(value = '') {
  return JSON_START_CHARS.has(String(value).trim().charAt(0));
}

function response_payload(payload) {
  if (payload?.data !== undefined && payload?.data !== null) return payload.data;
  return payload ?? {};
}

function parse_response_data(payload) {
  const raw_data = response_payload(payload);
  if (typeof raw_data !== 'string') return raw_data;

  const parsed_data = parse_json(raw_data);
  return typeof parsed_data === 'string' ? parse_json(parsed_data) : parsed_data;
}

function to_array(value) {
  return Array.isArray(value) ? value : [];
}

function normalize_text(value) {
  return String(value || '').trim();
}

function to_number(value) {
  const normalized = Number(value);
  return Number.isFinite(normalized) && normalized > 0 ? normalized : null;
}

function cache_key(name, params = {}) {
  const normalized_params = Object.entries(params)
    .sort(([left_key], [right_key]) => left_key.localeCompare(right_key))
    .map(([key, value]) => `${key}:${value ?? ''}`)
    .join('|');

  return `${name}|${normalized_params}`;
}

function assert_cached_get_url(url) {
  if (!ALLOWED_CACHED_GET_URLS.has(url)) {
    return;
  }
}

async function cached_get(name, url, params = {}) {
  assert_cached_get_url(url);
  const key = cache_key(name, params);
  if (sla_cache.has(key)) return sla_cache.get(key);

  const request = axios
    .get(url, get_request_config({ params }))
    .then(normalize_success_response);

  sla_cache.set(key, request);

  try {
    return await request;
  } catch {
    sla_cache.delete(key);
    return;
  }
}

function normalize_success_response(response) {
  const response_data = parse_response_data(response);
  if (response_data?.status === 'error') {
    this.show_error(response_data.message || 'Failed to load SLA data.');
  }
  return response_data?.data ?? response_data ?? [];
}

function targets_params({ project_id = null, is_active = true } = {}) {
  return {
    ...(is_active !== null && is_active !== undefined ? { is_active } : {}),
    ...(project_id ? { project_id } : {}),
  };
}

function parse_duration_parts(value) {
  const parts = String(value || '').trim().split(' ').filter(Boolean);
  const time_value = parts.length === 2 ? parts[1] : parts[0];
  const day_value = parts.length === 2 ? Number(parts[0]) : 0;
  const time_parts = String(time_value || '').split(':');

  if (!time_value || parts.length > 2 || time_parts.length !== 3) {
    return null;
  }

  const [hours, minutes, seconds] = time_parts.map(Number);
  const duration_values = [day_value, hours, minutes, seconds];
  return duration_values.every((part) => Number.isInteger(part) && part >= 0)
    ? { days: day_value, hours, minutes }
    : null;
}

function duration_label_parts(duration) {
  const parts = [];
  if (duration.days) parts.push(`${duration.days}d`);
  if (duration.hours) parts.push(`${duration.hours}h`);
  if (duration.minutes) parts.push(`${duration.minutes}m`);
  return parts;
}

export function format_sla_duration(value, fallback = 'Not set') {
  if (!value) return fallback;

  const duration = parse_duration_parts(value);
  return duration ? duration_label_parts(duration).join(' ') || '0m' : String(value);
}

function assignee_label(sla = {}) {
  const assignees = [
    { value: sla.escalation_role, label: (value) => `Role #${value}` },
    { value: sla.escalation_user, label: (value) => `User #${value}` },
  ];
  const assignee = assignees.find((item) => item.value);
  return assignee ? assignee.label(assignee.value) : '';
}

function escalation_label(sla = {}) {
  const duration = format_sla_duration(sla.escalation_duration, '');
  const assignee = assignee_label(sla);
  return [duration && `After ${duration}`, assignee].filter(Boolean).join(' - ') || 'Not configured';
}

function normalize_trigger(trigger = {}) {
  const normalized_trigger = {
    id: trigger.id,
    sla_config_id: to_number(trigger.sla_config),
    trigger_type: normalize_text(trigger.trigger_type),
    workflow_status_id: to_number(trigger.workflow_status),
    workflow_status_name: normalize_text(trigger.workflow_status_name),
    section_question_id: to_number(trigger.section_question),
    section_question_text: normalize_text(trigger.section_question_text),
    condition: trigger.trigger_condition || {},
  };

  return normalized_trigger;
}

function map_sla_target(sla = {}, { workflows = [], triggers = [] } = {}) {
  return {
    id: sla.id,
    name: normalize_text(sla.name),
    description: normalize_text(sla.description),
    project_id: to_number(sla.project),
    target_duration: sla.target_duration,
    warning_threshold: sla.warning_threshold,
    escalation_duration: sla.escalation_duration,
    target_label: format_sla_duration(sla.target_duration),
    warning_label: format_sla_duration(sla.warning_threshold),
    escalation_label: escalation_label(sla),
    workflows: to_array(workflows).filter((workflow) => workflow?.is_active !== false),
    triggers: to_array(triggers).map(normalize_trigger),
    status_label: sla.is_active === false ? 'Inactive' : 'Active',
    status_class: sla.is_active === false ? 'is-draft' : 'is-active',
    raw: sla,
  };
}

function settled_value(result = {}) {
  return result?.status === 'fulfilled' ? result.value : [];
}

function active_sla_bindings(config_data = {}) {
  return to_array(config_data?.sla_bindings).filter((binding) => binding?.is_active !== false);
}

function bound_sla_ids(config_data = {}) {
  return new Set(active_sla_bindings(config_data).map((binding) => to_number(binding?.sla_config)).filter(Boolean));
}

function status_matches_trigger(trigger = {}, { workflow_status_id = null, workflow_status_name = '' } = {}) {
  const status_id = to_number(workflow_status_id);
  const status_name = normalize_text(workflow_status_name).toLowerCase();

  if (!status_id && !status_name) return true;
  if (status_id && to_number(trigger.workflow_status_id) === status_id) return true;
  return Boolean(status_name && normalize_text(trigger.workflow_status_name).toLowerCase() === status_name);
}

export function clearXVerdictSlaCache() {
  sla_cache.clear();
}

export async function listXVerdictSlaConfigs(params = {}) {
  return to_array(await cached_get('list_sla_configs', LIST_SLA_CONFIGS_URL, targets_params(params)));
}

export async function getXVerdictSlaAssignedWorkflows(sla_config_id) {
  if (!sla_config_id) return [];
  return to_array(await cached_get('get_sla_assigned_workflows', GET_SLA_ASSIGNED_WORKFLOWS_URL, { sla_config_id }));
}

export async function getXVerdictSlaTriggers(sla_config_id) {
  if (!sla_config_id) return [];
  return to_array(await cached_get('get_sla_triggers', GET_SLA_TRIGGERS_URL, { sla_config_id }));
}

export async function getXVerdictSlaRuntimes(params = {}) {
  const clean_params = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== null && value !== undefined && value !== '')
  );
  return to_array(await cached_get('get_sla_runtimes', GET_SLA_RUNTIMES_URL, clean_params));
}

export async function updateXVerdictSlaRuntimeStatus(sla_runtime_id, status) {
  if (!sla_runtime_id || !status) return null;

  const response = await axios.post(
    UPDATE_SLA_RUNTIME_STATUS_URL,
    { sla_runtime_id, status },
    get_request_config()
  );
  clearXVerdictSlaCache();
  return normalize_success_response(response);
}

export async function getXVerdictSlaTargets(params = {}) {
  const sla_configs = await listXVerdictSlaConfigs(params);
  const workflow_results = await Promise.allSettled(
    sla_configs.map((sla) => getXVerdictSlaAssignedWorkflows(sla.id))
  );
  const trigger_results = await Promise.allSettled(
    sla_configs.map((sla) => getXVerdictSlaTriggers(sla.id))
  );

  return sla_configs.map((sla, index) => map_sla_target(sla, {
    workflows: settled_value(workflow_results[index]),
    triggers: settled_value(trigger_results[index]),
  }));
}

export async function getXVerdictEntityTypeSlaTargets({
  entity_type_id,
  project_id = null,
  sub_entity_type_id = null,
  workflow_status_id = null,
  workflow_status_name = '',
} = {}) {
  if (!entity_type_id) return [];

  const [config_data, targets] = await Promise.all([
    getXVerdictFullConfigByEntityType({ entity_type_id, project_id, sub_entity_type_id }),
    getXVerdictSlaTargets({ project_id, is_active: true }),
  ]);
  const ids = bound_sla_ids(config_data);
  const bound_targets = ids.size ? targets.filter((target) => ids.has(to_number(target.id))) : targets;

  return bound_targets.filter((target) =>
    !target.triggers.length
    || target.triggers.some((trigger) => status_matches_trigger(trigger, { workflow_status_id, workflow_status_name }))
  );
}
